import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const UserPreferences = () => {
  const [preferences, setPreferences] = useState({
    // Display & Interface
    theme: 'system',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12',
    timezone: 'America/New_York',
    currency: 'USD',
    numberFormat: 'US',
    
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    notificationFrequency: 'immediate',
    weeklyReports: true,
    monthlyReports: true,
    
    // Dashboard & View
    defaultDashboardView: 'overview',
    showWelcomeMessage: true,
    compactMode: false,
    sidebarCollapsed: false,
    itemsPerPage: 25,
    
    // Security & Privacy
    sessionTimeout: 30,
    twoFactorAuth: false,
    loginNotifications: true,
    dataRetention: 12,
    
    // Workflow
    autoSave: true,
    autoSaveInterval: 5,
    defaultApprovalWorkflow: true,
    requireComments: false,
    enableDraftMode: true
  });

  const themeOptions = [
    { value: 'light', label: 'Light', icon: 'Sun' },
    { value: 'dark', label: 'Dark', icon: 'Moon' },
    { value: 'system', label: 'System', icon: 'Monitor' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' }
  ];

  const dateFormatOptions = [
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'YYYY-MM-DD',
    'MM-DD-YYYY',
    'DD-MM-YYYY'
  ];

  const timezoneOptions = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Australia/Sydney'
  ];

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'JPY', label: 'Japanese Yen (¥)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' }
  ];

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    console.log('Saving preferences:', preferences);
    // TODO: Implement save functionality
  };

  const resetToDefaults = () => {
    // Reset to default values
    setPreferences({
      theme: 'system',
      language: 'en',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12',
      timezone: 'America/New_York',
      currency: 'USD',
      numberFormat: 'US',
      emailNotifications: true,
      pushNotifications: true,
      smsNotifications: false,
      notificationFrequency: 'immediate',
      weeklyReports: true,
      monthlyReports: true,
      defaultDashboardView: 'overview',
      showWelcomeMessage: true,
      compactMode: false,
      sidebarCollapsed: false,
      itemsPerPage: 25,
      sessionTimeout: 30,
      twoFactorAuth: false,
      loginNotifications: true,
      dataRetention: 12,
      autoSave: true,
      autoSaveInterval: 5,
      defaultApprovalWorkflow: true,
      requireComments: false,
      enableDraftMode: true
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">User Preferences</h2>
          <p className="text-muted-foreground">Customize your experience and default settings</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={resetToDefaults}>
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} iconName="Save">
            Save Changes
          </Button>
        </div>
      </div>
      {/* Display & Interface */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Display & Interface</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Theme Selection */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Theme
            </label>
            <div className="grid grid-cols-3 gap-2">
              {themeOptions?.map((theme) => (
                <button
                  key={theme?.value}
                  onClick={() => handlePreferenceChange('theme', theme?.value)}
                  className={`p-3 rounded-lg border transition-colors ${
                    preferences?.theme === theme?.value
                      ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon name={theme?.icon} size={16} className="mx-auto mb-1" />
                  <span className="text-xs font-medium">{theme?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Language */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Language
            </label>
            <select
              value={preferences?.language}
              onChange={(e) => handlePreferenceChange('language', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {languageOptions?.map((lang) => (
                <option key={lang?.value} value={lang?.value}>{lang?.label}</option>
              ))}
            </select>
          </div>

          {/* Date Format */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Date Format
            </label>
            <select
              value={preferences?.dateFormat}
              onChange={(e) => handlePreferenceChange('dateFormat', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {dateFormatOptions?.map((format) => (
                <option key={format} value={format}>{format}</option>
              ))}
            </select>
          </div>

          {/* Time Format */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Time Format
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePreferenceChange('timeFormat', '12')}
                className={`flex-1 p-2 text-sm rounded-md border transition-colors ${
                  preferences?.timeFormat === '12' ?'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50'
                }`}
              >
                12 Hour
              </button>
              <button
                onClick={() => handlePreferenceChange('timeFormat', '24')}
                className={`flex-1 p-2 text-sm rounded-md border transition-colors ${
                  preferences?.timeFormat === '24' ?'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50'
                }`}
              >
                24 Hour
              </button>
            </div>
          </div>

          {/* Timezone */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Timezone
            </label>
            <select
              value={preferences?.timezone}
              onChange={(e) => handlePreferenceChange('timezone', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {timezoneOptions?.map((tz) => (
                <option key={tz} value={tz}>{tz?.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          {/* Currency */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Default Currency
            </label>
            <select
              value={preferences?.currency}
              onChange={(e) => handlePreferenceChange('currency', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {currencyOptions?.map((currency) => (
                <option key={currency?.value} value={currency?.value}>{currency?.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Notifications */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Notifications</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Email Notifications</span>
                <p className="text-xs text-muted-foreground">Receive important updates via email</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.emailNotifications}
                onChange={(e) => handlePreferenceChange('emailNotifications', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Push Notifications</span>
                <p className="text-xs text-muted-foreground">Browser notifications for real-time updates</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.pushNotifications}
                onChange={(e) => handlePreferenceChange('pushNotifications', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">SMS Notifications</span>
                <p className="text-xs text-muted-foreground">Critical alerts via text message</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.smsNotifications}
                onChange={(e) => handlePreferenceChange('smsNotifications', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Weekly Reports</span>
                <p className="text-xs text-muted-foreground">Weekly summary of activities</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.weeklyReports}
                onChange={(e) => handlePreferenceChange('weeklyReports', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Monthly Reports</span>
                <p className="text-xs text-muted-foreground">Monthly financial summaries</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.monthlyReports}
                onChange={(e) => handlePreferenceChange('monthlyReports', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Notification Frequency
              </label>
              <select
                value={preferences?.notificationFrequency}
                onChange={(e) => handlePreferenceChange('notificationFrequency', e?.target?.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="immediate">Immediate</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Summary</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      {/* Dashboard & View */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Dashboard & View</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Default Dashboard View
            </label>
            <select
              value={preferences?.defaultDashboardView}
              onChange={(e) => handlePreferenceChange('defaultDashboardView', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="overview">Overview</option>
              <option value="financial">Financial</option>
              <option value="reports">Reports</option>
              <option value="tasks">Tasks</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Items Per Page
            </label>
            <select
              value={preferences?.itemsPerPage}
              onChange={(e) => handlePreferenceChange('itemsPerPage', parseInt(e?.target?.value))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-foreground">Show Welcome Message</span>
              <input
                type="checkbox"
                checked={preferences?.showWelcomeMessage}
                onChange={(e) => handlePreferenceChange('showWelcomeMessage', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-foreground">Compact Mode</span>
              <input
                type="checkbox"
                checked={preferences?.compactMode}
                onChange={(e) => handlePreferenceChange('compactMode', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-foreground">Collapse Sidebar</span>
              <input
                type="checkbox"
                checked={preferences?.sidebarCollapsed}
                onChange={(e) => handlePreferenceChange('sidebarCollapsed', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>
        </div>
      </div>
      {/* Security & Privacy */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Security & Privacy</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Session Timeout (minutes)
              </label>
              <Input
                type="number"
                value={preferences?.sessionTimeout}
                onChange={(e) => handlePreferenceChange('sessionTimeout', parseInt(e?.target?.value))}
                placeholder="30"
                min="5"
                max="480"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Data Retention (months)
              </label>
              <Input
                type="number"
                value={preferences?.dataRetention}
                onChange={(e) => handlePreferenceChange('dataRetention', parseInt(e?.target?.value))}
                placeholder="12"
                min="1"
                max="120"
              />
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Two-Factor Authentication</span>
                <p className="text-xs text-muted-foreground">Enhanced security for your account</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.twoFactorAuth}
                onChange={(e) => handlePreferenceChange('twoFactorAuth', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Login Notifications</span>
                <p className="text-xs text-muted-foreground">Get notified of account logins</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.loginNotifications}
                onChange={(e) => handlePreferenceChange('loginNotifications', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>
        </div>
      </div>
      {/* Workflow */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Workflow</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Auto Save</span>
                <p className="text-xs text-muted-foreground">Automatically save changes</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.autoSave}
                onChange={(e) => handlePreferenceChange('autoSave', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Default Approval Workflow</span>
                <p className="text-xs text-muted-foreground">Enable approval process by default</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.defaultApprovalWorkflow}
                onChange={(e) => handlePreferenceChange('defaultApprovalWorkflow', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Enable Draft Mode</span>
                <p className="text-xs text-muted-foreground">Save documents as drafts</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.enableDraftMode}
                onChange={(e) => handlePreferenceChange('enableDraftMode', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Auto Save Interval (minutes)
              </label>
              <Input
                type="number"
                value={preferences?.autoSaveInterval}
                onChange={(e) => handlePreferenceChange('autoSaveInterval', parseInt(e?.target?.value))}
                placeholder="5"
                min="1"
                max="60"
                disabled={!preferences?.autoSave}
              />
            </div>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Require Comments</span>
                <p className="text-xs text-muted-foreground">Mandatory comments for certain actions</p>
              </div>
              <input
                type="checkbox"
                checked={preferences?.requireComments}
                onChange={(e) => handlePreferenceChange('requireComments', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-border">
        <Button variant="outline" onClick={resetToDefaults}>
          Reset to Defaults
        </Button>
        <Button onClick={handleSave} iconName="Save">
          Save Preferences
        </Button>
      </div>
    </div>
  );
};

export default UserPreferences;